import { FC, useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Alert, Box, Snackbar, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';

import CustomButton from '../../../common/CustomButton';
import CreateTripModal from './components/CreateTripModal';
import AITripPlanningModal from './components/AITripPlanningModal';
import FullAccessModal from '../../../common/FullAccessModal';
import { PremiumFeature } from '../../../common/premiumFeatures';
import TripCard from './components/TripCard';
import TripMap from './components/TripMap';
import Calendar from './components/Calendar';
import SelectedDateTrips from './components/SelectedDateTrips';
import { useGetTripsQuery, useGetTripCoordinatesQuery, useDeleteTripMutation } from '../../../store/services/tripsApi';
import {
  PENDING_FLIGHT_TO_TRIP_STORAGE_KEY,
  PENDING_PLACE_TO_TRIP_STORAGE_KEY,
} from '../../../constants/discoverIdeaFlow';
import type { FlightToIdeaPayload } from '../../../types/flight.types';
import type { PlaceToIdeaPayload } from '../../../types/place.types';
import { tripDateRangeFromFlightPayload } from '../../../utils/flightToIdeaPayload';
import { tripDateRangeFromPlacePayload } from '../../../utils/placeToIdeaPayload';
import { useIsMobile } from '../../../hooks/useWindowWidth';
import { useBottomSuccessToast } from '../../../hooks/useBottomSuccessToast';

import styles from './MyTrips.module.scss';
import { AiIcon, DateIcon, FilterIcon } from '../../../components/Icons';
import TripsFiltersDrawer from './components/TripsFiltersDrawer';
import { TripsFilters } from './components/TripsFiltersDrawer/TripsFiltersDrawer';
import { TripsQueryParams, Trip } from '../../../types/trip.types';
import { isTripActive, isTripScheduled, isTripArchived } from '../../../utils/dateUtils';
import { useAppSelector } from '../../../hooks/redux';
import { getEffectiveDisplayFormat } from '../../../utils/premiumDisplayFormat';

const MyTrips: FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    show: showSuccessToast,
    onClose: closeSuccessToast,
    snackbarKey: successSnackbarKey,
    open: successToastOpen,
    message: successToastMessage,
  } = useBottomSuccessToast();
  const user = useAppSelector(state => state.user);
  const [openModal, setOpenModal] = useState(false);
  const [fromDiscoverFlow, setFromDiscoverFlow] = useState(false);
  const discoverOpenerRef = useRef(false);
  const [discoverTripInitialDates, setDiscoverTripInitialDates] = useState<{
    start_date: string;
    end_date: string;
  } | null>(null);
  const [pendingFlightToTripBody, setPendingFlightToTripBody] = useState<FlightToIdeaPayload | null>(null);
  const [pendingPlaceToTripBody, setPendingPlaceToTripBody] = useState<PlaceToIdeaPayload | null>(null);
  const [openAIPlanningModal, setOpenAIPlanningModal] = useState(false);
  const [openFullAccessModal, setOpenFullAccessModal] = useState(false);
  const [fullAccessFeature, setFullAccessFeature] = useState<PremiumFeature | undefined>();
  const [openFilters, setOpenFilters] = useState(false);
  const [selectedTab, setSelectedTab] = useState('scheduled');
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date>(new Date());

  const [mapExpanded, setMapExpanded] = useState(false);
  const [showCalendarInMapMode, setShowCalendarInMapMode] = useState(false);

  const [filters, setFilters] = useState<TripsFilters>({
    displayFormat: user.is_premium ? 'map' : 'calendar',
    sortBy: 'start_date',
    sortOrder: 'asc',
  });

  const effectiveDisplayFormat = getEffectiveDisplayFormat(filters.displayFormat, user.is_premium);

  const queryParams = useMemo((): TripsQueryParams => {
    const params: TripsQueryParams = {};

    const orderPrefix = filters.sortOrder === 'desc' ? '-' : '';
    switch (filters.sortBy) {
      case 'start_date':
        params.ordering = `${orderPrefix}start_date`;
        break;
      case 'created_at':
        params.ordering = `${orderPrefix}created_at`;
        break;
      case 'edited_at':
        params.ordering = `${orderPrefix}edited_at`;
        break;
    }

    return params;
  }, [filters]);

  const { data: trips, isLoading, refetch } = useGetTripsQuery(queryParams);
  const { data: tripCoordinates } = useGetTripCoordinatesQuery(queryParams);
  const [deleteTrip] = useDeleteTripMutation();

  useEffect(() => {
    const state = location.state as { tripDeletedToast?: boolean; deletedTripName?: string } | null;
    if (!state?.tripDeletedToast) return;

    const name = state.deletedTripName?.trim() || 'Trip';
    showSuccessToast(`Trip '${name}' was successfully deleted.`);
    navigate('/trips', { replace: true, state: {} });
  }, [location.state, navigate, showSuccessToast]);

  useEffect(() => {
    if (searchParams.get('fromDiscover') !== '1') {
      discoverOpenerRef.current = false;
      return;
    }
    if (discoverOpenerRef.current) return;
    discoverOpenerRef.current = true;

    const stripQuery = () => {
      navigate('/trips', { replace: true });
    };

    try {
      const flightRaw = sessionStorage.getItem(PENDING_FLIGHT_TO_TRIP_STORAGE_KEY);
      const placeRaw = sessionStorage.getItem(PENDING_PLACE_TO_TRIP_STORAGE_KEY);
      const raw = flightRaw ?? placeRaw;
      if (!raw) {
        stripQuery();
        return;
      }
      const parsed = JSON.parse(raw) as { body: FlightToIdeaPayload | PlaceToIdeaPayload };
      if (flightRaw) {
        const body = parsed.body as FlightToIdeaPayload;
        setPendingFlightToTripBody(body);
        setPendingPlaceToTripBody(null);
        setDiscoverTripInitialDates(tripDateRangeFromFlightPayload(body) ?? null);
      } else {
        const body = parsed.body as PlaceToIdeaPayload;
        setPendingPlaceToTripBody(body);
        setPendingFlightToTripBody(null);
        setDiscoverTripInitialDates(tripDateRangeFromPlacePayload(body) ?? null);
      }
      setFromDiscoverFlow(true);
      setOpenModal(true);
    } catch {
      try {
        sessionStorage.removeItem(PENDING_FLIGHT_TO_TRIP_STORAGE_KEY);
        sessionStorage.removeItem(PENDING_PLACE_TO_TRIP_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
    stripQuery();
  }, [searchParams, navigate]);

  const handleModalOpen = () => setOpenModal(true);
  const handleModalClose = () => {
    setOpenModal(false);
    if (fromDiscoverFlow) {
      setFromDiscoverFlow(false);
      setDiscoverTripInitialDates(null);
      setPendingFlightToTripBody(null);
      setPendingPlaceToTripBody(null);
      try {
        sessionStorage.removeItem(PENDING_FLIGHT_TO_TRIP_STORAGE_KEY);
        sessionStorage.removeItem(PENDING_PLACE_TO_TRIP_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  };

  const handleDiscoverFlowComplete = () => {
    setFromDiscoverFlow(false);
    setDiscoverTripInitialDates(null);
    setPendingFlightToTripBody(null);
    setPendingPlaceToTripBody(null);
    try {
      sessionStorage.removeItem(PENDING_FLIGHT_TO_TRIP_STORAGE_KEY);
      sessionStorage.removeItem(PENDING_PLACE_TO_TRIP_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  };
  const handleAIPlanningModalOpen = () => {
    if (!user.is_premium) {
      setFullAccessFeature('plan_trip_ai');
      setOpenFullAccessModal(true);
      return;
    }
    setOpenAIPlanningModal(true);
  };
  const handleAIPlanningModalClose = () => setOpenAIPlanningModal(false);
  const handleFullAccessModalClose = () => {
    setOpenFullAccessModal(false);
    if (fullAccessFeature === 'map') {
      setFilters(prev => ({ ...prev, displayFormat: 'calendar' }));
    }
    setFullAccessFeature(undefined);
  };
  const handleMergeSuccess = () => {
    refetch();
  };

  const toggleFilters = (newOpen: boolean) => {
    setOpenFilters(newOpen);
  };

  const handleTripCreated = async (trip?: Trip) => {
    await refetch();
    const tripName = trip?.name?.trim();
    showSuccessToast(
      tripName ? `Trip '${tripName}' was successfully created.` : 'Trip was successfully created.'
    );
  };

  const handleDeleteTrip = async (tripId: number) => {
    const tripName = trips?.find(t => t.id === tripId)?.name?.trim() || 'Trip';
    try {
      await deleteTrip(tripId.toString()).unwrap();
      await refetch();
      showSuccessToast(`Trip '${tripName}' was successfully deleted.`);
    } catch (error) {
      console.error('Failed to delete trip:', error);
    }
  };

  const handleTabChange = (_: React.MouseEvent<HTMLElement>, newTab: string | null) => {
    if (newTab !== null) setSelectedTab(newTab);
  };

  const handleFiltersChange = (newFilters: TripsFilters) => {
    if (!user.is_premium && newFilters.displayFormat === 'map') {
      setFullAccessFeature('map');
      setOpenFullAccessModal(true);
      return;
    }

    setFilters(newFilters);
    if (newFilters.displayFormat !== 'map') {
      setMapExpanded(false);
      setShowCalendarInMapMode(false);
    }
  };

  const handleCalendarDateSelect = (date: Date | null) => {
    if (date) {
      setSelectedCalendarDate(date);
    }
  };

  const handleDateIconClick = () => {
    setShowCalendarInMapMode(!showCalendarInMapMode);
    setMapExpanded(false);
  };

  const handleMapExpand = () => {
    setMapExpanded(!mapExpanded);
    setShowCalendarInMapMode(false);
  };

  const filteredTrips = useMemo(() => {
    if (!trips) return [];

    return trips.filter((trip: Trip) => {
      if (selectedTab === 'scheduled') {
        return isTripScheduled(trip.end_date);
      } else {
        return isTripArchived(trip.end_date);
      }
    });
  }, [trips, selectedTab]);

  const filteredTripCoordinates = useMemo(() => {
    if (!tripCoordinates || !filteredTrips.length) return [];

    const idByName = new Map(filteredTrips.map(t => [t.name, t.id]));
    const filteredTripNames = new Set(filteredTrips.map(trip => trip.name));

    return tripCoordinates
      .filter(coordinate => filteredTripNames.has(coordinate.name))
      .map(coordinate => ({
        ...coordinate,
        trip_id: idByName.get(coordinate.name),
      }));
  }, [tripCoordinates, filteredTrips]);

  const tripMapKey = useMemo(
    () =>
      `${selectedTab}-${filteredTripCoordinates.map(c => `${c.trip_id ?? ''}:${c.name}:${c.destination_countries?.length ?? 0}`).join('|') || 'empty'}`,
    [selectedTab, filteredTripCoordinates]
  );

  const checkIfTripActive = (trip: Trip) => {
    return isTripActive(trip.start_date, trip.end_date);
  };

  const getContentWrapperClass = () => {
    let className = styles.contentWrapper;

    return className;
  };

  const shouldShowTabs = () => {
    return effectiveDisplayFormat === 'map' || effectiveDisplayFormat === 'calendar';
  };

  const shouldShowTripsList = () => {
    if (effectiveDisplayFormat === 'map') {
      return !showCalendarInMapMode && !mapExpanded;
    }
    return effectiveDisplayFormat === 'calendar';
  };

  const shouldShowExpandedMap = () => {
    return effectiveDisplayFormat === 'map' && mapExpanded && !showCalendarInMapMode;
  };

  const shouldShowCalendarInMapMode = () => {
    return effectiveDisplayFormat === 'map' && showCalendarInMapMode;
  };

  return (
    <>
      <CreateTripModal
        open={openModal}
        onClose={handleModalClose}
        onSuccess={handleTripCreated}
        initialStartDate={discoverTripInitialDates?.start_date}
        initialEndDate={discoverTripInitialDates?.end_date}
        pendingFlightToTripBody={fromDiscoverFlow ? pendingFlightToTripBody : null}
        pendingPlaceToTripBody={fromDiscoverFlow ? pendingPlaceToTripBody : null}
        onDiscoverFlowComplete={handleDiscoverFlowComplete}
      />
      <AITripPlanningModal
        open={openAIPlanningModal}
        onClose={handleAIPlanningModalClose}
        onSuccess={handleTripCreated}
      />
      <FullAccessModal
        open={openFullAccessModal}
        feature={fullAccessFeature}
        onClose={handleFullAccessModalClose}
      />

      <TripsFiltersDrawer
        openFilters={openFilters}
        toggleFilters={toggleFilters}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <Box className={styles.myTrips}>
        <Box className={styles.tripsHeader}>
          <Box className={styles.tripsHeaderLeft}>
            <Typography
              variant={isMobile ? 'h6Semibold' : 'h4Bold'}
              sx={{ color: '#222B3A', fontSize: isMobile ? '20px' : '30px' }}
            >
              My Trips
            </Typography>
            <Typography
              variant="h10"
              sx={{ color: '#5E6A81' }}
            >
              Overview of your past and planned trips
            </Typography>
          </Box>

          <Box className={styles.buttonsWrapper}>
            <CustomButton
              variant="contained"
              onClick={handleModalOpen}
              customClass={styles.customButton}
            >
              {isMobile ? <Typography variant="h3">+</Typography> : '+ Create new'}
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={handleAIPlanningModalOpen}
              customClass={styles.aiButton}
              startIcon={<AiIcon />}
            >
              {!isMobile && 'Plan a Trip with AI'}
            </CustomButton>
          </Box>
        </Box>

        {!isMobile && (
          <Box className={styles.tripsNavWrapper}>
            <Box className={styles.tabsScroll}>
              <ToggleButtonGroup
                value={selectedTab}
                exclusive
                onChange={handleTabChange}
                className={styles.toggleGroup}
              >
                <ToggleButton
                  value="scheduled"
                  className={`${selectedTab === 'scheduled' && styles.selected} ${styles.toggleButton}`}
                >
                  {selectedTab === 'scheduled' ? (
                    <Typography variant="h10Bold">Scheduled</Typography>
                  ) : (
                    <Typography variant="h10Semibold">Scheduled</Typography>
                  )}
                </ToggleButton>
                <ToggleButton
                  value="archived"
                  className={`${selectedTab === 'archived' && styles.selected} ${styles.toggleButton}`}
                >
                  {selectedTab === 'archived' ? (
                    <Typography variant="h10Bold">Archived</Typography>
                  ) : (
                    <Typography variant="h10Semibold">Archived</Typography>
                  )}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box
              className={styles.filterIconWrapper}
              onClick={() => toggleFilters(true)}
            >
              <FilterIcon size={20} />
              <Typography
                sx={{ color: '#5E6A81' }}
                variant="h10Semibold"
              >
                Filters
              </Typography>
            </Box>
          </Box>
        )}

        <Box className={getContentWrapperClass()}>
          {!isMobile && (
            <>
              <Box className={styles.tripsListContainer}>
                {isLoading && <Typography>Loading trips...</Typography>}

                {filteredTrips && filteredTrips.length > 0 ? (
                  <Box className={styles.tripsList}>
                    {filteredTrips.map(trip => (
                      <TripCard
                        key={trip.id}
                        trip={trip}
                        variant="full"
                        isActive={checkIfTripActive(trip)}
                        onDelete={handleDeleteTrip}
                        onMergeSuccess={handleMergeSuccess}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box className={styles.emptyState}>
                    <Typography variant="h7">
                      {selectedTab === 'scheduled' ? 'No scheduled trips yet' : 'No archived trips'}
                    </Typography>
                    <Typography
                      variant="h11"
                      sx={{ color: '#5E6A81' }}
                    >
                      {selectedTab === 'scheduled'
                        ? 'Create your first trip!'
                        : 'Your completed trips will appear here'}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                className={`${styles.mapContainer} ${
                  effectiveDisplayFormat === 'map' ? styles.mapContainerMap : styles.mapContainerCalendar
                }`}
              >
                {effectiveDisplayFormat === 'map' ? (
                  <TripMap
                    key={tripMapKey}
                    coordinates={filteredTripCoordinates}
                    showZoomControls={true}
                    showExpandButton={false}
                    isMobile={false}
                  />
                ) : (
                  <Box className={styles.calendarContainer}>
                    <Box className={styles.calendarSection}>
                      <Calendar
                        trips={filteredTrips}
                        selectedDate={selectedCalendarDate}
                        onDateSelect={handleCalendarDateSelect}
                      />
                    </Box>
                    <Box className={styles.tripsSection}>
                      <SelectedDateTrips
                        trips={filteredTrips}
                        selectedDate={selectedCalendarDate}
                        onDeleteTrip={handleDeleteTrip}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          )}

          {isMobile && (
            <>
              {effectiveDisplayFormat === 'map' && !showCalendarInMapMode && !mapExpanded && (
                <Box className={styles.mapSection}>
                  <TripMap
                    key={tripMapKey}
                    coordinates={filteredTripCoordinates}
                    isExpanded={false}
                    onMapExpand={handleMapExpand}
                    showZoomControls={false}
                    showExpandButton={true}
                    isMobile={true}
                  />
                </Box>
              )}

              {shouldShowTabs() && (
                <Box className={styles.tripsNavWrapper}>
                  <Box className={styles.tabsScroll}>
                    <ToggleButtonGroup
                      value={selectedTab}
                      exclusive
                      onChange={handleTabChange}
                      className={styles.toggleGroup}
                    >
                      <ToggleButton
                        value="scheduled"
                        className={`${selectedTab === 'scheduled' && styles.selected} ${styles.toggleButton}`}
                      >
                        {selectedTab === 'scheduled' ? (
                          <Typography variant="h11Bold">Scheduled</Typography>
                        ) : (
                          <Typography variant="h11Semibold">Scheduled</Typography>
                        )}
                      </ToggleButton>
                      <ToggleButton
                        value="archived"
                        className={`${selectedTab === 'archived' && styles.selected} ${styles.toggleButton}`}
                      >
                        {selectedTab === 'archived' ? (
                          <Typography variant="h11Bold">Archived</Typography>
                        ) : (
                          <Typography variant="h11Semibold">Archived</Typography>
                        )}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Box
                    className={styles.filterIconWrapper}
                    onClick={() => toggleFilters(true)}
                  >
                    <FilterIcon size={20} />
                  </Box>
                </Box>
              )}

              <Box
                className={`${styles.contentArea} ${
                  effectiveDisplayFormat === 'calendar' ? styles.contentAreaCalendar : ''
                }`}
              >
                {shouldShowExpandedMap() && (
                  <Box className={styles.expandedMapContainer}>
                    <TripMap
                      key={tripMapKey}
                      coordinates={filteredTripCoordinates}
                      isExpanded={true}
                      onMapExpand={handleMapExpand}
                      showZoomControls={false}
                      showExpandButton={true}
                      isMobile={true}
                    />
                  </Box>
                )}

                {shouldShowCalendarInMapMode() && (
                  <Box className={styles.calendarContainer}>
                    <Box className={styles.calendarSection}>
                      <Calendar
                        trips={filteredTrips}
                        selectedDate={selectedCalendarDate}
                        onDateSelect={handleCalendarDateSelect}
                      />
                    </Box>
                    <Box className={styles.tripsSection}>
                      <SelectedDateTrips
                        trips={filteredTrips}
                        selectedDate={selectedCalendarDate}
                        onDeleteTrip={handleDeleteTrip}
                      />
                    </Box>
                  </Box>
                )}

                {effectiveDisplayFormat === 'calendar' && (
                  <Box className={styles.calendarContainer}>
                    <Box className={styles.calendarSection}>
                      <Calendar
                        trips={filteredTrips}
                        selectedDate={selectedCalendarDate}
                        onDateSelect={handleCalendarDateSelect}
                      />
                    </Box>
                    <Box className={styles.tripsSection}>
                      <SelectedDateTrips
                        trips={filteredTrips}
                        selectedDate={selectedCalendarDate}
                        onDeleteTrip={handleDeleteTrip}
                      />
                    </Box>
                  </Box>
                )}

                {shouldShowTripsList() && (
                  <Box className={styles.tripsListContainer}>
                    {isLoading && <Typography>Loading trips...</Typography>}

                    {filteredTrips && filteredTrips.length > 0 ? (
                      <Box className={styles.tripsList}>
                        {filteredTrips.map(trip => (
                          <TripCard
                            key={trip.id}
                            trip={trip}
                            variant="full"
                            isActive={checkIfTripActive(trip)}
                            onDelete={handleDeleteTrip}
                            onMergeSuccess={handleMergeSuccess}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Box className={styles.emptyState}>
                        <Typography variant="h7">
                          {selectedTab === 'scheduled' ? 'No scheduled trips yet' : 'No archived trips'}
                        </Typography>
                        <Typography
                          variant="h11"
                          sx={{ color: '#5E6A81' }}
                        >
                          {selectedTab === 'scheduled'
                            ? 'Create your first trip!'
                            : 'Your completed trips will appear here'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>

        {isMobile && effectiveDisplayFormat === 'map' && (
          <Box
            className={styles.fixedDateIcon}
            onClick={handleDateIconClick}
          >
            <DateIcon
              size={24}
              color="white"
            />
          </Box>
        )}
      </Box>

      <Snackbar
        key={successSnackbarKey}
        open={successToastOpen}
        autoHideDuration={5000}
        onClose={closeSuccessToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSuccessToast}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successToastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MyTrips;
